import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  children: React.ReactNode;
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
}
const sizeStyles = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'max-w-full mx-4 sm:mx-6 lg:mx-auto lg:max-w-4xl'
};
export function Modal({
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
  children,
  showCloseButton = true,
  closeOnOverlayClick = true
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  // Focus trap
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
      const handleTab = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };
      document.addEventListener('keydown', handleTab);
      firstElement?.focus();
      return () => document.removeEventListener('keydown', handleTab);
    }
  }, [isOpen]);
  return <AnimatePresence data-id="element-465">
      {isOpen && <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-id="element-466">
          {/* Overlay */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="absolute inset-0 bg-surface-900/50 backdrop-blur-sm" onClick={closeOnOverlayClick ? onClose : undefined} aria-hidden="true" data-id="element-467" />

          {/* Modal */}
          <motion.div ref={modalRef} initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} role="dialog" aria-modal="true" aria-labelledby={title ? 'modal-title' : undefined} aria-describedby={description ? 'modal-description' : undefined} className={`
              relative w-full ${sizeStyles[size]}
              bg-white rounded-t-2xl sm:rounded-2xl shadow-modal
              max-h-[90vh] sm:max-h-[85vh] overflow-hidden
              flex flex-col
              m-0 sm:m-4
            `} data-id="element-468">
            {/* Header */}
            {(title || showCloseButton) && <div className="flex items-start justify-between p-4 sm:p-6 pb-0 flex-shrink-0" data-id="element-469">
                <div className="flex-1 pr-4" data-id="element-470">
                  {title && <h2 id="modal-title" className="text-heading-md sm:text-heading-lg text-surface-900" data-id="element-471">
                      {title}
                    </h2>}
                  {description && <p id="modal-description" className="mt-1 text-body-sm text-surface-500" data-id="element-472">
                      {description}
                    </p>}
                </div>
                {showCloseButton && <button onClick={onClose} className="
                      p-2 -mr-2 -mt-2
                      text-surface-400 hover:text-surface-600
                      rounded-lg hover:bg-surface-100
                      transition-colors duration-150
                      flex-shrink-0
                    " aria-label="Close modal" data-id="element-473">
                    <XIcon className="w-5 h-5" data-id="element-474" />
                  </button>}
              </div>}

            {/* Content */}
            <div className="p-4 sm:p-6 overflow-y-auto flex-1 overscroll-contain" data-id="element-475">
              {children}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
// Modal Footer for action buttons
export interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}
export function ModalFooter({
  children,
  className = ''
}: ModalFooterProps) {
  return <div className={`
        flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3
        pt-4 border-t border-surface-100
        ${className}
      `} data-id="element-476">
      {children}
    </div>;
}